import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuthStore } from '../../core/stores/authStore.js';
import { logout } from '../../core/auth/session.js';
import { Avatar } from '../ui/Avatar.jsx';
import { Button } from '../ui/Button.jsx';
import { sidebarNavItems, sidebarFooterItems, isNavItemVisible } from './navConfig.js';

function LogoutConfirm({ onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-[var(--radius-card)] bg-white p-6 shadow-[var(--shadow-lg)]">
        <p className="mb-6 text-center text-lg text-brand">Are you sure you want to logout?</p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} className="flex-1">
            Log Out
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ className?: string }} props
 */
export function Sidebar({ className = '' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAuthStore((s) => s.currentUser);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const visibleItems = sidebarNavItems.filter(isNavItemVisible);

  const handleNavigate = (item) => {
    if (item.videoType) {
      navigate(item.path, { state: { yocasType: item.videoType } });
      return;
    }
    navigate(item.path);
  };

  const handleLogout = async () => {
    setConfirmLogout(false);
    await logout();
    navigate('/welcome');
  };

  const isActive = (item) => item.match(location.pathname, location.state);

  return (
    <>
      <aside
        className={`md:fixed md:inset-y-0 md:left-0 md:z-40 flex h-screen w-64 flex-col border-r border-brand/5 bg-gradient-to-br from-surface via-white/95 to-accent-peach/10 p-4 ${className}`}
        aria-label="Main navigation"
      >
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mb-6 flex justify-center px-2 min-h-0 min-w-0"
        >
          <img
            src="/images/LogoDark.png"
            alt="Yocas"
            className="h-10 w-auto object-contain"
          />
        </button>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <button
                key={item.label}
                type="button"
                onClick={() => handleNavigate(item)}
                className={`relative flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors min-h-[44px] ${
                  active
                    ? 'bg-brand/10 font-semibold text-brand'
                    : 'text-brand hover:bg-brand/5'
                }`}
              >
                {active && (
                  <span className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-brand" />
                )}
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${active ? 'bg-brand/15' : 'bg-brand/8'}`}>
                  <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
                </span>
                <span className="text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="mt-auto border-t border-brand/10 pt-4">
          {currentUser && (
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="mb-3 flex w-full items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-brand/5 min-h-[44px]"
            >
              <Avatar
                src={currentUser.profilePictureUrl}
                name={currentUser.name || ''}
                size="sm"
                className="h-9 w-9"
              />
              <div className="min-w-0 text-left">
                <p className="truncate text-sm font-medium text-brand">{currentUser.name}</p>
                <p className="truncate text-xs text-text-secondary">View profile</p>
              </div>
            </button>
          )}

          {sidebarFooterItems.map((item) => {
            const Icon = item.icon;
            if (item.action === 'logout') {
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => setConfirmLogout(true)}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-brand transition-colors hover:bg-brand/5 min-h-[44px]"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/8">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            }
            const active = location.pathname.startsWith(item.path);
            return (
              <button
                key={item.label}
                type="button"
                onClick={() => navigate(item.path)}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors min-h-[44px] ${
                  active ? 'bg-brand/10 font-semibold text-brand' : 'text-brand hover:bg-brand/5'
                }`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/8">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="text-sm">{item.label}</span>
              </button>
            );
          })}
        </div>
      </aside>

      {confirmLogout && (
        <LogoutConfirm
          onConfirm={handleLogout}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </>
  );
}
